import React, { FC, useMemo, useRef, useState } from 'react'
import { Form, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import { Box, Button, CircularProgress, Divider, Grid, IconButton, Typography } from '@mui/material'
import { Delete } from '@mui/icons-material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useNavigate } from 'react-router-dom'
import { TextField as MuiTextField } from '@mui/material'
import { TextField, FormCheckbox, Dropdown } from 'src/components/ui'
import { useReleaseListRequest } from 'src/hooks/api'
import { useDropdownItemFactory } from 'src/hooks/ui'
import { PromptInput, PromptCheckboxOption, PromptLink } from 'src/api'

export interface PromptFormValues {
  title: string
  description: string
  minAppVersion: string
  skippable: boolean
  showNewUsers: boolean
  startDate: Date | null
}

interface PromptFormProps {
  initialValues?: Partial<PromptFormValues>
  initialCheckboxOptions?: PromptCheckboxOption[]
  initialLinks?: PromptLink[]
  // When the prompt already has responses, title/description/checkboxes/links are locked.
  locked?: boolean
  isLoading?: boolean
  submitLabel?: string
  // Where the Cancel button navigates (create → list, edit → details).
  cancelTo?: string
  onSubmit: (payload: PromptInput) => void
}

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  description: Yup.string().required('Description is required'),
  startDate: Yup.date().nullable().typeError('Enter a valid date'),
  minAppVersion: Yup.string()
})

type LinkRow = PromptLink & { _key: string }

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

export const PromptForm: FC<PromptFormProps> = ({
  initialValues,
  initialCheckboxOptions = [],
  initialLinks = [],
  locked = false,
  isLoading = false,
  submitLabel = 'Save',
  cancelTo = '/prompts',
  onSubmit
}) => {
  const navigate = useNavigate()
  const linkKeyCounter = useRef<number>(0)
  const nextLinkKey = () => {
    linkKeyCounter.current += 1
    return `link_${linkKeyCounter.current}`
  }
  const [options, setOptions] = useState<PromptCheckboxOption[]>(initialCheckboxOptions)
  const [links, setLinks] = useState<LinkRow[]>(initialLinks.map((link) => ({ ...link, _key: nextLinkKey() })))
  const [newOptionLabel, setNewOptionLabel] = useState<string>('')
  const [error, setError] = useState<string>('')

  const formik = useFormik<PromptFormValues>({
    initialValues: {
      title: initialValues?.title || '',
      description: initialValues?.description || '',
      minAppVersion: initialValues?.minAppVersion || '',
      skippable: initialValues?.skippable || false,
      showNewUsers: initialValues?.showNewUsers || false,
      startDate: initialValues?.startDate || null
    },
    validationSchema,
    onSubmit: (values) => {
      if (options.length === 0) {
        setError('Add at least one checkbox option')
        return
      }
      if (options.some((option) => !option.label.trim())) {
        setError('Every checkbox option needs a label')
        return
      }
      if (links.some((link) => !link.name.trim() || !link.url.trim())) {
        setError('Every link needs a name and a URL')
        return
      }
      setError('')

      const payload: PromptInput = {
        title: values.title,
        description: values.description,
        checkboxOptions: options,
        links: links.length ? links.map(({ _key, ...link }) => link) : null,
        skippable: values.skippable,
        showNewUsers: values.showNewUsers,
        minAppVersion: values.minAppVersion || null,
        startDate: values.startDate ? (values.startDate as Date).toISOString() : null
      }
      onSubmit(payload)
    }
  })

  const { values, setFieldValue, submitForm, errors, touched } = formik

  // App-version options come from the release list — same source as the Release info page.
  const { data: releases } = useReleaseListRequest({ params: { limit: 100, sort: 'id:desc' } })
  const appVersionOptions = useMemo(() => {
    const versionOptions: { number: string; label: string }[] = [{ number: '', label: 'None' }]
    ;(releases || []).forEach((release) => versionOptions.push({ number: release.number, label: release.number }))
    // Preserve an existing value (e.g. an old release) that is no longer in the list.
    if (values.minAppVersion && !versionOptions.some((option) => option.number === values.minAppVersion)) {
      versionOptions.push({ number: values.minAppVersion, label: values.minAppVersion })
    }
    return versionOptions
  }, [releases, values.minAppVersion])
  const appVersionItemFactory = useDropdownItemFactory({ params: { labelProp: 'label', valueProp: 'number' } })

  const addOption = () => {
    const label = newOptionLabel.trim()
    if (!label) return
    const existingKeys = new Set(options.map((option) => option.key))
    let key = slugify(label) || `option_${options.length + 1}`
    while (existingKeys.has(key)) key = `${key}_1`
    setOptions([...options, { key, label }])
    setNewOptionLabel('')
  }

  const handleNewOptionKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return

    event.preventDefault()
    addOption()
  }

  const updateOption = (index: number, patch: Partial<PromptCheckboxOption>) => {
    setOptions(options.map((option, i) => (i === index ? { ...option, ...patch } : option)))
  }

  const removeOption = (index: number) => setOptions(options.filter((_, i) => i !== index))

  const addLink = () => setLinks([...links, { name: '', url: '', _key: nextLinkKey() }])
  const updateLink = (index: number, patch: Partial<PromptLink>) => {
    setLinks(links.map((link, i) => (i === index ? { ...link, ...patch } : link)))
  }
  const removeLink = (index: number) => setLinks(links.filter((_, i) => i !== index))

  return (
    <FormikProvider value={formik}>
      <Form>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField name="title" label="Title" required TextFieldProps={{ disabled: locked }} />
          </Grid>
          <Grid item xs={12}>
            <TextField name="description" label="Description" required TextFieldProps={{ multiline: true, minRows: 3, disabled: locked }} />
          </Grid>
        </Grid>
      </Form>

      <Box mt={3}>
        <Typography variant="subtitle1" fontWeight={600}>
          Checkbox options
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Shown to the user as checkboxes when the prompt is displayed.
        </Typography>
        {options.map((option, index) => (
          <Grid container alignItems="center" spacing={1} key={option.key} mt={0.5}>
            <Grid item xs={10}>
              <MuiTextField fullWidth size="small" label="Label" value={option.label} disabled={locked} onChange={(event) => updateOption(index, { label: event.target.value })} />
            </Grid>
            <Grid item xs={2}>
              <IconButton color="error" disabled={locked} onClick={() => removeOption(index)}>
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        {!locked && (
          <Grid container alignItems="center" spacing={1} mt={0.5}>
            <Grid item xs={9}>
              <MuiTextField
                fullWidth
                size="small"
                label="Add option"
                placeholder="e.g. I understand the Heat Index rules"
                value={newOptionLabel}
                onChange={(event) => setNewOptionLabel(event.target.value)}
                onKeyDown={handleNewOptionKeyDown}
              />
            </Grid>
            <Grid item xs={3}>
              <Button variant="outlined" onClick={addOption}>
                Add
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>

      <Box mt={3}>
        <Typography variant="subtitle1" fontWeight={600}>
          Links (optional)
        </Typography>
        {links.map((link, index) => (
          <Grid container alignItems="center" spacing={1} key={link._key} mt={0.5}>
            <Grid item xs={12} sm={4}>
              <MuiTextField fullWidth size="small" label="Name" value={link.name} disabled={locked} onChange={(event) => updateLink(index, { name: event.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MuiTextField fullWidth size="small" label="URL" value={link.url} disabled={locked} onChange={(event) => updateLink(index, { url: event.target.value })} />
            </Grid>
            <Grid item xs={2}>
              <IconButton color="error" disabled={locked} onClick={() => removeLink(index)}>
                <Delete />
              </IconButton>
            </Grid>
          </Grid>
        ))}
        {!locked && (
          <Button variant="outlined" sx={{ mt: 1 }} onClick={addLink}>
            Add link
          </Button>
        )}
      </Box>

      <Box mt={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start date (optional)"
                value={values.startDate}
                onChange={(date) => setFieldValue('startDate', date)}
                renderInput={(props) => (
                  <MuiTextField {...props} fullWidth size="small" error={touched.startDate && !!errors.startDate} helperText={touched.startDate && (errors.startDate as string)} />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Dropdown
              list={appVersionOptions}
              name="minAppVersion"
              label="Minimum app version"
              value={values.minAppVersion}
              renderValue={(value) => value || 'None'}
              SelectProps={{ displayEmpty: true }}
              menuItemFactory={appVersionItemFactory}
              onChange={(_, value) => setFieldValue('minAppVersion', value)}
              onBlur={(_, value) => setFieldValue('minAppVersion', value)}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} mt={0.5}>
          <Grid item>
            <FormCheckbox name="skippable" label="Skippable (offer “Skip”)" view="switch" />
          </Grid>
          <Grid item>
            <FormCheckbox name="showNewUsers" label="Show to users who join later" view="switch" />
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
          {error}
        </Typography>
      )}

      <Divider sx={{ mt: 3 }} />

      <Box mt={2} display="flex" gap={2}>
        <Button variant="contained" onClick={submitForm} disabled={isLoading}>
          {isLoading ? <CircularProgress color="info" size={26} /> : submitLabel}
        </Button>
        <Button variant="outlined" onClick={() => navigate(cancelTo)}>
          Cancel
        </Button>
      </Box>
    </FormikProvider>
  )
}
